import { AspectMaintainer, RangeSlider, Select } from "@clintonelec/react-storybook";
import {
	type ManualWbMode, manualWbModeOptions, type MirrorImage, mirrorImageOptions, WhiteBalanceMode,
	whiteBalanceModeOptions
} from "Data/Objects/Camera";
import { produce } from "immer";
import type { ImageSettingsState } from "Interfaces";
import { type Dispatch, memo, type SetStateAction } from "react";

type AppearanceProps = {
	localSettings: ImageSettingsState;
	setLocalSettings: Dispatch<SetStateAction<ImageSettingsState>>;
};

function Appearance(props: AppearanceProps) {
	const { localSettings, setLocalSettings } = props;
	const {
		mirrorImage, brightness, contrast, color, sharpness, whiteBalanceMode, manualWbMode
	} = localSettings.appearance;

	const handleMirrorImageSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setLocalSettings(produce(localSettings, draft => {
			draft.appearance.mirrorImage = value as MirrorImage;
		}));
	};

	const handleBrightnessChange = (value: number | number[]) => {
		if (Array.isArray(value)) {
			return;
		}

		setLocalSettings(produce(localSettings, draft => {
			draft.appearance.brightness = value;
		}));
	};

	const handleContrastChange = (value: number | number[]) => {
		if (Array.isArray(value)) {
			return;
		}

		setLocalSettings(produce(localSettings, draft => {
			draft.appearance.contrast = value;
		}));
	};

	const handleColorChange = (value: number | number[]) => {
		if (Array.isArray(value)) {
			return;
		}

		setLocalSettings(produce(localSettings, draft => {
			draft.appearance.color = value;
		}));
	};

	const handleSharpnessChange = (value: number | number[]) => {
		if (Array.isArray(value)) {
			return;
		}

		setLocalSettings(produce(localSettings, draft => {
			draft.appearance.sharpness = value;
		}));
	};

	const handleWhiteBalanceModeSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setLocalSettings(produce(localSettings, draft => {
			draft.appearance.whiteBalanceMode = value as WhiteBalanceMode;
		}));
	};

	const handleManualWbModeSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setLocalSettings(produce(localSettings, draft => {
			draft.appearance.manualWbMode = value as ManualWbMode;
		}));
	};

	return (
		<div className="two-column-card-content">
			<div className="left-content">
				<AspectMaintainer>
					<div className="camera-snapshot"></div>
				</AspectMaintainer>
				<div className="form-row">&nbsp;</div>
				<div className="form-row">
					<span>Mirror Image</span>
					<Select
						allowClear={ false }
						className="form-select"
						onSelect={ handleMirrorImageSelect }
						options={ mirrorImageOptions }
						value={ mirrorImage }
					/>
				</div>
			</div>
			<div className="right-content">
				<div className="form-row">
					<span>Brightness</span>
					<div className="form-slider-wrapper">
						<RangeSlider
							hideInputCharMessage
							inputDisplay
							max={ 30 }
							min={ 0 }
							onChange={ handleBrightnessChange }
							value={ brightness }
						/>
					</div>
				</div>
				<div className="form-row">
					<span>Contrast</span>
					<div className="form-slider-wrapper">
						<RangeSlider
							hideInputCharMessage
							inputDisplay
							max={ 30 }
							min={ 0 }
							onChange={ handleContrastChange }
							value={ contrast }
						/>
					</div>
				</div>
				<div className="form-row">
					<span>Color</span>
					<div className="form-slider-wrapper">
						<RangeSlider
							hideInputCharMessage
							inputDisplay
							max={ 30 }
							min={ 0 }
							onChange={ handleColorChange }
							value={ color }
						/>
					</div>
				</div>
				<div className="form-row">
					<span>Sharpness</span>
					<div className="form-slider-wrapper">
						<RangeSlider
							hideInputCharMessage
							inputDisplay
							max={ 15 }
							min={ 0 }
							onChange={ handleSharpnessChange }
							value={ sharpness }
						/>
					</div>
				</div>
				<div className="form-row">&nbsp;</div>
				<div className="form-row">
					<span>White Balance Mode</span>
					<Select
						allowClear={ false }
						className="form-select"
						onSelect={ handleWhiteBalanceModeSelect }
						options={ whiteBalanceModeOptions }
						value={ whiteBalanceMode }
					/>
				</div>
				<div className="form-row">
					<span>Manual WB Mode</span>
					<Select
						allowClear={ false }
						className="form-select"
						disabled={ whiteBalanceMode !== WhiteBalanceMode.MANUAL }
						onSelect={ handleManualWbModeSelect }
						options={ manualWbModeOptions }
						value={ manualWbMode }
					/>
				</div>
			</div>
		</div>
	);
}

export default memo(Appearance);
